import { z } from 'zod';

export const ExecutionStatus = z.enum([
  'pending',
  'running',
  // Paused at an `approval` step until a human decides (ADR-031); not terminal.
  'waiting',
  'succeeded',
  'failed',
  'cancelled',
]);
export type ExecutionStatus = z.infer<typeof ExecutionStatus>;

export const StepStatus = z.enum(['running', 'waiting', 'succeeded', 'failed', 'skipped']);
export type StepStatus = z.infer<typeof StepStatus>;

export const StepSummary = z.object({
  key: z.string(),
  type: z.string(),
  sequence: z.number().int(),
  status: StepStatus,
  attempt: z.number().int(),
  startedAt: z.iso.datetime(),
  completedAt: z.iso.datetime().nullable(),
  /** Safe, schema-defined step output (never secrets). */
  output: z.record(z.string(), z.unknown()).nullable(),
  error: z.object({ code: z.string(), message: z.string() }).nullable(),
});
export type StepSummary = z.infer<typeof StepSummary>;

export const ExecutionSummary = z.object({
  executionId: z.string().uuid(),
  workflow: z.object({ key: z.string(), version: z.number().int() }),
  status: ExecutionStatus,
  /** "stopped" = ended early by a guard with a structured result (still a success). */
  outcome: z.enum(['completed', 'stopped']).nullable(),
  /** Effective classification of the data the execution touched (null = none recorded). */
  classification: z.enum(['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED']).nullable(),
  principal: z.object({ userId: z.string().uuid(), email: z.string().nullable() }),
  client: z.object({ clientId: z.string(), kind: z.string() }),
  correlationId: z.string(),
  traceId: z.string().nullable(),
  createdAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
});
export type ExecutionSummary = z.infer<typeof ExecutionSummary>;

export const ExecutionDetail = ExecutionSummary.extend({
  input: z.record(z.string(), z.unknown()),
  output: z.record(z.string(), z.unknown()).nullable(),
  error: z.object({ code: z.string(), message: z.string() }).nullable(),
  steps: z.array(StepSummary),
});
export type ExecutionDetail = z.infer<typeof ExecutionDetail>;

export const ExecutionList = z.object({
  items: z.array(ExecutionSummary),
  nextCursor: z.string().nullable(),
});
export type ExecutionList = z.infer<typeof ExecutionList>;

/** Input of the generic system workflow SYSTEM_CONNECTIVITY_TEST. */
export const ConnectivityTestInput = z.object({
  message: z
    .string()
    .min(1)
    .max(200)
    .default('ping')
    .describe('Echoed back as a SHA-256 checksum.'),
  delayMs: z
    .number()
    .int()
    .min(0)
    .max(30_000)
    .default(0)
    .describe('Optional deterministic delay (restart/durability testing).'),
});
export type ConnectivityTestInput = z.infer<typeof ConnectivityTestInput>;

/** Structured MCP result of execute_test_workflow. */
export const TestWorkflowResult = z.object({
  executionId: z.string().uuid(),
  workflow: z.object({ key: z.string(), version: z.number().int() }),
  status: ExecutionStatus,
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  correlationId: z.string(),
  stepsExecuted: z.array(
    z.object({ key: z.string(), status: StepStatus, completedAt: z.iso.datetime().nullable() }),
  ),
  output: z.record(z.string(), z.unknown()).nullable(),
  /** True when the bounded wait elapsed before completion; call get_execution later. */
  stillRunning: z.boolean(),
});
export type TestWorkflowResult = z.infer<typeof TestWorkflowResult>;

export const WorkflowListItem = z.object({
  key: z.string(),
  version: z.number().int(),
  domain: z.string(),
  name: z.string(),
  description: z.string(),
  executableVia: z.string().describe('MCP tool that runs this workflow.'),
  inputSchema: z
    .record(z.string(), z.unknown())
    .optional()
    .describe('JSON Schema of the workflow input.'),
});
export const WorkflowList = z.object({ workflows: z.array(WorkflowListItem) });
export type WorkflowList = z.infer<typeof WorkflowList>;

export const PlatformStatus = z.object({
  status: z.enum(['ok', 'degraded', 'fail']),
  version: z.string(),
  environment: z.string(),
  serverTime: z.iso.datetime(),
  components: z.array(z.object({ name: z.string(), status: z.enum(['ok', 'degraded', 'fail']) })),
});
export type PlatformStatus = z.infer<typeof PlatformStatus>;

/** Result of starting a workflow from MCP (sync-or-handle, ADR-009). */
export const WorkflowRunResult = z.object({
  executionId: z.string().uuid(),
  workflow: z.object({ key: z.string(), version: z.number().int() }),
  status: ExecutionStatus,
  outcome: z.enum(['completed', 'stopped']).nullable(),
  output: z.record(z.string(), z.unknown()).nullable(),
  error: z.object({ code: z.string(), message: z.string() }).nullable(),
  stepsExecuted: z.array(
    z.object({ key: z.string(), status: StepStatus, completedAt: z.iso.datetime().nullable() }),
  ),
  correlationId: z.string(),
  stillRunning: z.boolean(),
});
export type WorkflowRunResult = z.infer<typeof WorkflowRunResult>;

export const ExecuteWorkflowInput = z.object({
  workflow: z.string().min(3).max(64).describe('Workflow key, e.g. "EA_ANALYSE_ISSUE".'),
  version: z
    .number()
    .int()
    .positive()
    .optional()
    .describe('Exact version; latest published when omitted.'),
  input: z
    .record(z.string(), z.unknown())
    .default({})
    .describe('Workflow input (see list_workflows).'),
});
